import { FC } from 'react';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import { H2, P, PMedium } from 'shared/components/Typography';
import { Spacer } from 'shared/components/Layout';
import { Wrapper } from 'shared/components/Wrapper';
import SVG from 'shared/components/SVG';
import UtilService from 'shared/services/util.service';
import { nevada, valencia } from 'shared/config/Colors';

import { SpecialOrderApprovalStatusEnum } from '../../enums/SpecialOrderApprovalStatusEnum';
import { ACKTypeSheetContainer } from '../ACKTypeSheetContainer';
import ApprovedACKIcon from '../ApprovedACKIcon';

interface SpecialOrderApprovalSheetProps {
  approved: boolean;
  disapproved?: boolean;
  status?: SpecialOrderApprovalStatusEnum;
  modifiedBy?: string | null;
  approvedOnUtc?: string;
  disapprovedOnUtc?: string;
}

const SpecialOrderApprovalSheet: FC<SpecialOrderApprovalSheetProps> = ({
  approved,
  disapproved,
  status,
  modifiedBy,
  approvedOnUtc,
  disapprovedOnUtc,
}) => {
  const isDisapproved =
    disapproved ?? status === SpecialOrderApprovalStatusEnum.DISAPPROVED;

  const actionDate = approved ? approvedOnUtc : disapprovedOnUtc;

  return (
    <ACKTypeSheetContainer data-testid="ack-sheet--special-order-approval">
      <Wrapper inlineFlex middle>
        <H2 fontSize={24}>Special Order Approval</H2>
        {approved && (
          <ApprovedACKIcon data-testid="ack-sheet-approved--special-order-approval" />
        )}
        {isDisapproved && (
          <Wrapper
            inlineFlex
            middle
            data-testid="ack-sheet-disapproved--special-order-approval"
          >
            <Spacer w="20px" />
            <SVG icon={<CloseIcon width={16} height={16} />} color={valencia} />
          </Wrapper>
        )}
        {(approved || isDisapproved) && (modifiedBy || actionDate) && (
          <Wrapper inlineFlex middle>
            <Spacer w="12px" />
            <PMedium color={nevada}>
              {modifiedBy}
              {modifiedBy && actionDate ? ' — ' : ''}
              {actionDate &&
                UtilService.formatDate({ date: actionDate, asElement: false })}
            </PMedium>
          </Wrapper>
        )}
      </Wrapper>

      <Spacer h="24px" />

      <P>
        You have specified doors or drawers which must be ordered from an
        outside source. Your approval must be received within 2 working days.
        Upon receipt of your approval, Plato will place an order for the
        outsourced item(s).
      </P>

      <P>
        Any delay in returning this signed form will delay ordering the
        outsourced item(s) which may result in an extended delivery. Once the
        order for the approved outsourced item(s) has been placed by Plato, any
        change orders requiring a change/deletion of the outsourced item(s) will
        be the responsibility of the dealer.
      </P>

      <Spacer h="20px" />
    </ACKTypeSheetContainer>
  );
};

export default SpecialOrderApprovalSheet;
